/**
 * Legal page table of contents.
 * Auto-detects policy/legal page content (native Shopify policy pages via
 * .shopify-policy__body, custom legal pages via [data-toc-content]).
 *
 * Only top-level (h2) headings are used, matching Anthropic's constitution
 * page pattern.
 *
 * The sidebar is inserted as a sibling of the content, absolutely
 * positioned so its top edge lines up with the top of the body text (never
 * a fixed arbitrary offset), and its horizontal offset is measured from the
 * content's own bounding box so the content itself is never resized or
 * recentered. Inside that, the actual nav list is `position: sticky` so it
 * follows you as you scroll past it.
 *
 * The sidebar's own height is capped to whatever room is actually left
 * inside the content below where it starts, so it can never visually
 * overhang past the end of the body text (e.g. into the footer). If there
 * isn't enough room for a usable sidebar at all, it falls back to the
 * mobile dropdown instead of rendering something cramped.
 *
 * The list is height-constrained with its own scrollbar regardless of how
 * many headings it has. Small arrows fade in above/below it whenever
 * there's more content in that direction, and the currently active section
 * auto-scrolls into view -- but only when it isn't already visible, and
 * never while the user is actively scrolling the list themselves.
 */
(function () {
  'use strict';

  var SIDEBAR_WIDTH = 220;
  var SIDEBAR_GAP = 32;
  var SIDEBAR_MIN_MARGIN = 16;
  var SIDEBAR_MIN_HEIGHT = 120;
  var MANUAL_SCROLL_PAUSE_MS = 1500;
  var HEADER_FALLBACK_PX = 60;

  function slugify(text) {
    return text
      .toString()
      .trim()
      .toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  function debounceFrame(fn) {
    var scheduled = false;
    return function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        fn();
      });
    };
  }

  function buildToc(contentEl) {
    try {
      init(contentEl);
    } catch (err) {
      if (window.console && console.error) {
        console.error('[legal-toc] failed to initialize:', err);
      }
    }
  }

  function init(contentEl) {
    if (!contentEl || contentEl.dataset.tocInitialized) return;
    contentEl.dataset.tocInitialized = 'true';

    var headings = contentEl.querySelectorAll('h2');
    if (headings.length < 2) return;

    assignHeadingIds(headings);

    var parent = contentEl.parentNode;
    if (!parent) return;
    if (window.getComputedStyle(parent).position === 'static') {
      parent.style.position = 'relative';
    }

    // Mobile indicator -- a slim sticky bar showing the current section,
    // inserted directly above the content in normal document flow (so it
    // never disturbs the content's own centering, and doesn't need the
    // complex positioning math the desktop sidebar needs).
    var mobileBar = document.createElement('div');
    mobileBar.className = 'legal-toc-mobile';

    var mobileToggle = document.createElement('button');
    mobileToggle.type = 'button';
    mobileToggle.className = 'legal-toc-mobile__toggle';
    mobileToggle.setAttribute('aria-expanded', 'false');

    var mobileLabel = document.createElement('span');
    mobileLabel.className = 'legal-toc-mobile__label';
    mobileLabel.appendChild(document.createTextNode("You're in: "));

    var mobileCurrent = document.createElement('span');
    mobileCurrent.className = 'legal-toc-mobile__current';
    mobileCurrent.textContent = headings[0].textContent;
    mobileLabel.appendChild(mobileCurrent);

    var mobileChevron = document.createElement('span');
    mobileChevron.className = 'legal-toc-mobile__chevron';
    mobileChevron.setAttribute('aria-hidden', 'true');

    mobileToggle.appendChild(mobileLabel);
    mobileToggle.appendChild(mobileChevron);

    var mobileListWrap = document.createElement('div');
    mobileListWrap.className = 'legal-toc-mobile__list-wrap';

    var mobileScrollBox = document.createElement('div');
    mobileScrollBox.className = 'legal-toc-mobile__scroll';

    var mobileList = document.createElement('ul');
    mobileList.className = 'legal-toc-mobile__list';
    mobileScrollBox.appendChild(mobileList);
    mobileListWrap.appendChild(mobileScrollBox);
    mobileListWrap.inert = true;

    mobileBar.appendChild(mobileToggle);
    mobileBar.appendChild(mobileListWrap);

    // On native policy pages there's a language selector rendered above the
    // content. On mobile, we pull it into a shared row alongside the TOC
    // bar (language left, TOC right) placed right before the divider --
    // sticky positioning is per-element, so only the TOC bar sticks as you
    // scroll while the language selector scrolls away beside it. On
    // desktop this doesn't apply at all -- the language selector must stay
    // exactly where it originally was, so we track its home position and
    // move it back whenever desktop mode is active.
    var languageEl = document.querySelector('.legal-policy-meta__language');
    var divider = document.querySelector('.legal-policy-divider');
    var languageHomeParent = languageEl ? languageEl.parentNode : null;
    var languageHomeNextSibling = languageEl ? languageEl.nextSibling : null;
    var topRow = null;
    var languageInMobileRow = false;

    function moveLanguageToMobileRow() {
      if (!languageEl || !divider || languageInMobileRow) return;
      if (!topRow) {
        topRow = document.createElement('div');
        topRow.className = 'legal-toc-mobile-top-row';
      }
      topRow.appendChild(languageEl);
      topRow.appendChild(mobileBar);
      divider.parentNode.insertBefore(topRow, divider);
      languageInMobileRow = true;
    }

    function restoreLanguageToHome() {
      if (!languageEl || !languageInMobileRow) return;
      if (languageHomeNextSibling) {
        languageHomeParent.insertBefore(languageEl, languageHomeNextSibling);
      } else {
        languageHomeParent.appendChild(languageEl);
      }
      parent.insertBefore(mobileBar, contentEl);
      if (topRow && topRow.parentNode) {
        topRow.parentNode.removeChild(topRow);
      }
      languageInMobileRow = false;
    }

    if (!languageEl || !divider) {
      parent.insertBefore(mobileBar, contentEl);
    }

    function closeMobileList() {
      mobileListWrap.inert = true;
      mobileToggle.setAttribute('aria-expanded', 'false');
      mobileBar.classList.remove('is-open');
    }

    mobileToggle.addEventListener('click', function () {
      var willOpen = mobileListWrap.inert;
      mobileListWrap.inert = !willOpen;
      mobileToggle.setAttribute('aria-expanded', String(willOpen));
      mobileBar.classList.toggle('is-open', willOpen);
    });

    // Desktop sidebar -- absolutely positioned wrapper aligned to the
    // content's own top edge, with a sticky inner nav so it follows scroll.
    var outer = document.createElement('div');
    outer.className = 'legal-toc-outer';
    outer.style.position = 'absolute';
    outer.style.width = SIDEBAR_WIDTH + 'px';

    var sidebar = document.createElement('nav');
    sidebar.className = 'legal-toc';
    sidebar.setAttribute('aria-label', 'Table of contents');

    var arrowUp = document.createElement('span');
    arrowUp.className = 'legal-toc__arrow legal-toc__arrow--up';
    arrowUp.setAttribute('aria-hidden', 'true');

    var scrollBox = document.createElement('div');
    scrollBox.className = 'legal-toc__scroll';

    var list = document.createElement('ul');
    list.className = 'legal-toc__list';
    scrollBox.appendChild(list);

    var arrowDown = document.createElement('span');
    arrowDown.className = 'legal-toc__arrow legal-toc__arrow--down';
    arrowDown.setAttribute('aria-hidden', 'true');

    sidebar.appendChild(arrowUp);
    sidebar.appendChild(scrollBox);
    sidebar.appendChild(arrowDown);
    outer.appendChild(sidebar);
    parent.insertBefore(outer, contentEl);

    var allLinks = [];
    var mobileLinks = [];

    headings.forEach(function (h) {
      var goTo = function (e) {
        e.preventDefault();
        h.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };

      var li = document.createElement('li');
      li.className = 'legal-toc__item';
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      a.addEventListener('click', goTo);
      li.appendChild(a);
      list.appendChild(li);
      allLinks.push(a);

      var mLi = document.createElement('li');
      mLi.className = 'legal-toc-mobile__item';
      var mA = document.createElement('a');
      mA.href = '#' + h.id;
      mA.textContent = h.textContent;
      mA.addEventListener('click', function (e) {
        goTo(e);
        closeMobileList();
      });
      mLi.appendChild(mA);
      mobileList.appendChild(mLi);
      mobileLinks.push(mA);
    });

    function setActive(id) {
      allLinks.forEach(function (a) {
        a.classList.toggle('is-active', a.getAttribute('href') === '#' + id);
      });
      mobileLinks.forEach(function (a) {
        var isMatch = a.getAttribute('href') === '#' + id;
        a.classList.toggle('is-active', isMatch);
        if (isMatch) {
          mobileCurrent.textContent = a.textContent;
        }
      });
    }

    function updateArrows() {
      var atTop = scrollBox.scrollTop <= 1;
      var atBottom = scrollBox.scrollTop + scrollBox.clientHeight >= scrollBox.scrollHeight - 1;
      arrowUp.classList.toggle('is-visible', !atTop);
      arrowDown.classList.toggle('is-visible', !atBottom);
    }

    var manualScrollTimer = null;
    var isManualScroll = false;
    scrollBox.addEventListener(
      'scroll',
      function () {
        updateArrows();
        isManualScroll = true;
        window.clearTimeout(manualScrollTimer);
        manualScrollTimer = window.setTimeout(function () {
          isManualScroll = false;
        }, MANUAL_SCROLL_PAUSE_MS);
      },
      { passive: true }
    );

    function smoothScrollTo(container, targetTop, duration) {
      var startTop = container.scrollTop;
      var distance = targetTop - startTop;
      if (Math.abs(distance) < 1) return;
      var startTime = null;

      function step(timestamp) {
        if (startTime === null) startTime = timestamp;
        var elapsed = timestamp - startTime;
        var progress = Math.min(elapsed / duration, 1);
        var eased = 1 - Math.pow(1 - progress, 3);
        container.scrollTop = startTop + distance * eased;
        updateArrows();
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      }
      window.requestAnimationFrame(step);
    }

    function scrollActiveIntoView(link) {
      var linkRect = link.getBoundingClientRect();
      var boxRect = scrollBox.getBoundingClientRect();
      var currentOffsetWithinBox = linkRect.top - boxRect.top + scrollBox.scrollTop;
      var targetTop = currentOffsetWithinBox - scrollBox.clientHeight / 2 + linkRect.height / 2;
      targetTop = Math.max(0, Math.min(targetTop, scrollBox.scrollHeight - scrollBox.clientHeight));
      smoothScrollTo(scrollBox, targetTop, 300);
    }

    function getHeaderHeightPx() {
      var headerEl = document.querySelector('header-component');
      if (headerEl) {
        var rect = headerEl.getBoundingClientRect();
        if (rect.height > 0) return rect.height;
      }
      var cssVar = window.getComputedStyle(document.body).getPropertyValue('--header-height');
      var parsed = parseFloat(cssVar);
      return isNaN(parsed) ? HEADER_FALLBACK_PX : parsed;
    }

    function positionSidebar() {
      var contentRect = contentEl.getBoundingClientRect();
      var parentRect = parent.getBoundingClientRect();
      var left = contentRect.left - parentRect.left - SIDEBAR_WIDTH - SIDEBAR_GAP;
      var top = contentRect.top - parentRect.top;

      var headerHeightPx = getHeaderHeightPx();
      var sidebarTopPx = headerHeightPx + 24;
      var idealMaxHeightPx = window.innerHeight - headerHeightPx - 48;

      // The sidebar can never be taller than the room actually left inside
      // the content below where it starts -- otherwise its own box can
      // visually overhang past the end of the body text into whatever
      // comes after it (e.g. the footer), even though sticky positioning
      // itself won't scroll it there.
      var availableTrackPx = contentRect.height - top - sidebarTopPx;
      var fitsInMargin = contentRect.left - SIDEBAR_WIDTH - SIDEBAR_GAP >= SIDEBAR_MIN_MARGIN;
      var hasRoomToBeUseful = availableTrackPx >= SIDEBAR_MIN_HEIGHT;

      if (!fitsInMargin || !hasRoomToBeUseful) {
        outer.hidden = true;
        mobileBar.hidden = false;
        moveLanguageToMobileRow();
        return;
      }

      outer.hidden = false;
      mobileBar.hidden = true;
      restoreLanguageToHome();
      outer.style.left = left + 'px';
      outer.style.top = top + 'px';
      // The outer wrapper's height matches the content exactly -- it must
      // never extend past the end of the body text.
      outer.style.height = contentRect.height + 'px';

      sidebar.style.top = sidebarTopPx + 'px';
      sidebar.style.maxHeight = Math.min(idealMaxHeightPx, availableTrackPx) + 'px';
      updateArrows();
    }

    var scheduleReposition = debounceFrame(positionSidebar);

    positionSidebar();
    window.addEventListener('resize', scheduleReposition);
    window.addEventListener('orientationchange', scheduleReposition);

    if ('ResizeObserver' in window) {
      new ResizeObserver(scheduleReposition).observe(contentEl);

      var headerEl = document.querySelector('header-component');
      if (headerEl) {
        new ResizeObserver(scheduleReposition).observe(headerEl);
      }
    }

    function isLinkVisible(link) {
      var linkRect = link.getBoundingClientRect();
      var boxRect = scrollBox.getBoundingClientRect();
      return linkRect.top >= boxRect.top && linkRect.bottom <= boxRect.bottom;
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;

            setActive(entry.target.id);

            if (isManualScroll) return;
            var activeLink = list.querySelector('a.is-active');
            if (!activeLink || isLinkVisible(activeLink)) return;

            scrollActiveIntoView(activeLink);
          });
        },
        { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
      );
      headings.forEach(function (h) {
        observer.observe(h);
      });
    }
  }

  function assignHeadingIds(headings) {
    var used = {};
    headings.forEach(function (h, i) {
      if (h.id) {
        used[h.id] = true;
        return;
      }
      var base = slugify(h.textContent) || 'section-' + i;
      var id = base;
      var n = 1;
      while (used[id]) {
        id = base + '-' + n;
        n++;
      }
      used[id] = true;
      h.id = id;
    });
  }

  function start() {
    var targets = document.querySelectorAll('.shopify-policy__body, [data-toc-content]');
    targets.forEach(buildToc);
  }

  // Always wait for DOMContentLoaded rather than running immediately if
  // the document already looks "ready". This script loads via <script
  // defer>, which is guaranteed to execute before DOMContentLoaded fires --
  // but legal-policy-page.liquid's inline script also waits for that same
  // event to build the divider and reposition the language selector, and
  // it registered its listener earlier (during HTML parsing). Listening
  // here too (rather than checking readyState and running early) ensures
  // we always run after it, so the divider/language elements we depend on
  // actually exist by the time we look for them.
  document.addEventListener('DOMContentLoaded', start);
})();
